import { Box, Flex, Heading, Icon, Link, ListItem, Text, UnorderedList } from '@chakra-ui/react';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';

import {
  ConsentAgreementTypeEnum,
  ConsentNameEnumType,
  useCurrentConsentAgreementTextQuery,
  useSaveConsentMutation,
} from '../../../../../../../gql/generated/graphql';
import {
  MarketplaceSubEvent,
  OfferActionSurface,
} from '../../../../../../../services/analytics/marketplaceEvents';
import { usePatchLeadByExternalIdOffersOfferId } from '../../../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdOffersOfferId';
import { logger } from '../../../../../../../services/sentry';
import getSearchPhoneNumbers from '../../../../../../../utils/getSearchPhoneNumbers';
import { formatMoney } from '../../../../../../../utils/helpers';
import { payoffLabel, payoffNoun } from '../../../hooks/usePayoffDisplay';
import { SETTLEMENT_FINE_PRINT, getNetCash } from '../../../netCash';
import { useOfferAnalytics } from '../OfferAnalyticsContext';
import { useOfferActionFeedback } from '../useOfferActionFeedback';
import { MarketplaceOffer, OfferCardView, isOfferStale } from '../utils';
import OfferActionModal from './OfferActionModal';
import OfferModalActions from './OfferModalActions';
import StaleOfferBanner from './StaleOfferBanner';

type AcceptOfferModalProps = {
  card: OfferCardView;
  surface: OfferActionSurface;
  liveOffers: MarketplaceOffer[];
  externalId: string;
  dealId: string;
  payoffAmount: number | null;
  payoffIsTrusted: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const ReceiptRow = ({ label, amount }: { label: string; amount: string }) => (
  <Flex justify="space-between" fontSize="13px" color="gray.600">
    <Text>{label}</Text>
    <Text sx={{ fontVariantNumeric: 'tabular-nums' }}>{amount}</Text>
  </Flex>
);

const AcceptReceipt = ({
  offerAmount,
  payoffAmount,
  payoffIsTrusted,
}: {
  offerAmount: number;
  payoffAmount: number;
  payoffIsTrusted: boolean;
}) => {
  const netCash = getNetCash(offerAmount, payoffAmount);
  const phone = getSearchPhoneNumbers();

  return (
    <Box bg="grayBackground" borderRadius="8px" px="14px" py="12px" mb="16px">
      <Flex direction="column" gap="6px">
        <ReceiptRow label="Offer" amount={formatMoney(offerAmount, { noCents: true })} />
        <ReceiptRow
          label={payoffLabel(payoffIsTrusted)}
          amount={`- ${formatMoney(payoffAmount, { noCents: true })}`}
        />
      </Flex>
      <Box borderTopWidth="1px" borderTopColor="grayMediumBackground" my="10px" />
      <Flex justify="space-between" align="center" gap="10px">
        <Text
          fontSize="12px"
          fontWeight="700"
          letterSpacing="0.04em"
          textTransform="uppercase"
          color="grayMedium"
        >
          {netCash.label}
        </Text>
        <Text
          fontSize="20px"
          fontWeight="700"
          color={netCash.color}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {netCash.amount}
        </Text>
      </Flex>
      <Text fontSize="12px" color="grayLight" lineHeight="1.5" mt="10px">
        This is an estimate based on your current offer and {payoffNoun(payoffIsTrusted)}.{' '}
        {SETTLEMENT_FINE_PRINT}
      </Text>
      <Text fontSize="12px" color="grayLight" lineHeight="1.5" mt="6px">
        If this payoff doesn't look right, call us at{' '}
        <Link href={`tel:+1${phone.replace(/\D/g, '')}`} fontWeight="600" color="oceanBoatBlue">
          {phone}
        </Link>
        .
      </Text>
    </Box>
  );
};

const AcceptOfferModal = ({
  card,
  surface,
  liveOffers,
  externalId,
  dealId,
  payoffAmount,
  payoffIsTrusted,
  onClose,
  onSuccess,
}: AcceptOfferModalProps) => {
  const { mutate, isPending } = usePatchLeadByExternalIdOffersOfferId();
  const { refetchOffers, notifyOfferChanged } = useOfferActionFeedback(externalId);
  const { trackOfferEvent } = useOfferAnalytics();
  const isStale = isOfferStale(card.offer, liveOffers);

  const showsReceipt = payoffAmount != null;
  // v2 drops the payoff-difference clause; only valid when the receipt states that math instead.
  const consentVersion = showsReceipt ? 2 : 1;
  const { data: consentData } = useCurrentConsentAgreementTextQuery({
    variables: {
      agreementType: ConsentAgreementTypeEnum.MarketplaceAcceptance,
      version: consentVersion,
    },
  });
  const consentText = consentData?.currentConsentAgreementText?.text ?? '';
  const [saveConsent] = useSaveConsentMutation();

  const eventContext = {
    offerId: card.offer.id,
    dealerLabel: card.dealerLabel,
    dealerAmount: card.amount,
    surface,
  };

  const handleAccept = () => {
    mutate(
      {
        externalId,
        offerId: card.offer.id,
        data: { action: 'accept' },
      },
      {
        onSuccess: () => {
          trackOfferEvent(MarketplaceSubEvent.OfferAcceptConfirmed, eventContext);
          saveConsent({
            variables: {
              type: ConsentNameEnumType.MarketplaceAcceptance,
              dealId,
              version: consentVersion,
            },
          }).catch((error) => {
            logger.error('AcceptOfferModal', 'saveConsent failed', { dealId }, error);
          });
          refetchOffers();
          onSuccess();
          onClose();
        },
        onError: () => {
          trackOfferEvent(MarketplaceSubEvent.OfferActionFailed, {
            ...eventContext,
            actionType: 'accept',
          });
          notifyOfferChanged();
          onClose();
        },
      },
    );
  };

  return (
    <OfferActionModal isOpen onClose={isPending ? () => undefined : onClose}>
      <Box px={{ base: '24px', md: '32px' }} pt={{ base: '28px', md: '32px' }}>
        {isStale && <StaleOfferBanner />}
        <Text
          as="span"
          display="block"
          fontSize="12px"
          fontWeight="700"
          letterSpacing="0.04em"
          textTransform="uppercase"
          color="grayMedium"
          mb="6px"
        >
          Accepting offer
        </Text>
        <Heading
          as="span"
          display="block"
          fontSize="36px"
          fontWeight="700"
          lineHeight="1.1"
          color="leaseEndBlue"
        >
          {formatMoney(card.amount)}
        </Heading>
        <Text fontSize="12.5px" color="grayLight" mt="6px">
          from {card.dealerLabel}
        </Text>
      </Box>

      <Box
        borderTopWidth="1px"
        borderTopColor="gainsboro"
        mx={{ base: '24px', md: '32px' }}
        my="16px"
      />

      <Box px={{ base: '24px', md: '32px' }} pb={{ base: '24px', md: '32px' }}>
        {showsReceipt && (
          <AcceptReceipt
            offerAmount={card.amount}
            payoffAmount={payoffAmount}
            payoffIsTrusted={payoffIsTrusted}
          />
        )}

        <UnorderedList spacing="6px" fontSize="13px" color="gray.600" lineHeight="1.5" mb="16px">
          <ListItem>This closes all your other active offers and starts the sale process.</ListItem>
          <ListItem>
            We'll share your name, email, and phone with the buyer so they can reach out.
          </ListItem>
        </UnorderedList>

        {consentText && (
          <Text fontSize="13px" color="gray.600" lineHeight="1.5" mb="24px">
            {consentText}
          </Text>
        )}

        <OfferModalActions
          flexDirection={{ base: 'column-reverse', md: 'row' }}
          onCancel={onClose}
          onPrimary={handleAccept}
          primaryLabel={`Accept ${formatMoney(card.amount)}`}
          isPending={isPending}
          isPrimaryDisabled={isStale}
          primaryIcon={<Icon as={FaCheck} boxSize="12px" />}
        />
      </Box>
    </OfferActionModal>
  );
};

export default AcceptOfferModal;
